import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  MessageSquare,
  Heart,
  ThumbsUp,
  Check,
  X,
  Image as ImageIcon,
  Calendar,
  BarChart3,
  Upload,
  GripVertical,
  ZoomIn,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogManagement = ({ language = 'fr' }) => {
  // Articles state
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [images, setImages] = useState([]); // Array of {id, url, caption, order}
  const [status, setStatus] = useState('draft');
  const [scheduledFor, setScheduledFor] = useState(''); // ISO datetime-local string (YYYY-MM-DDTHH:mm)
  const [saving, setSaving] = useState(false);
  
  // Drag & drop
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const dropZoneRef = useRef(null);
  const contentTextareaRef = useRef(null);

  // Internal-link picker
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkPickerQuery, setLinkPickerQuery] = useState('');
  
  // Image preview
  const [previewImage, setPreviewImage] = useState(null);
  
  // Comments moderation
  const [pendingComments, setPendingComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  // Seed starter articles
  const [seeding, setSeeding] = useState(false);

  // ── Auto-save brouillon (anti-perte de travail) ─────────────────────────
  // - localStorage : sauvegarde miroir à chaque modification (résistant aux
  //   crashs, fermetures de navigateur, perte de connexion)
  // - backend    : enregistrement automatique en statut « draft » toutes les
  //   45 secondes après une modification (jamais publié automatiquement)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | pending | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // ID effectif utilisé pour l'auto-save (créé à la volée si nouvel article)
  const autoSaveArticleIdRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveDirtyRef = useRef(false);
  // Flag : on désactive temporairement l'auto-save quand on ouvre/ferme l'éditeur
  // pour ne pas déclencher de save sur l'initialisation des champs.
  const autoSavePausedRef = useRef(true);
  // Brouillon local en attente de restauration (cas crash/fermeture imprévue)
  const [pendingLocalDraft, setPendingLocalDraft] = useState(null);

  const localDraftKey = (id) => `blog_local_draft_${id || 'new'}`;
  const ALL_LOCAL_DRAFT_KEY_PREFIX = 'blog_local_draft_';

  // Cherche dans localStorage un brouillon local non vidé (= éditeur fermé sans save)
  const findUnsavedLocalDraft = (preferredId = null) => {
    try {
      const ids = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(ALL_LOCAL_DRAFT_KEY_PREFIX)) ids.push(k);
      }
      if (!ids.length) return null;
      const preferredKey = localDraftKey(preferredId);
      const key = ids.includes(preferredKey) ? preferredKey : ids[0];
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.title && !parsed?.content) return null;
      parsed._key = key;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const fetchArticles = useCallback(async (signal) => {
    setLoading(true);
    const token = localStorage.getItem('dermascan_token');
    if (!token) {
      console.warn('No auth token found - skipping article fetch');
      setLoading(false);
      return;
    }
    const params = activeTab !== 'all' ? `?status=${activeTab}` : '';

    // Retry once on transient network errors
    const attempt = async (retriesLeft) => {
      try {
        const response = await axios.get(`${API_URL}/blog/articles${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });
        setArticles(response.data.articles || []);
      } catch (error) {
        if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
          // Request was aborted (tab changed) — ignore silently
          return;
        }
        const isNetwork = !error.response; // network error / timeout
        if (isNetwork && retriesLeft > 0) {
          await new Promise((r) => setTimeout(r, 800));
          return attempt(retriesLeft - 1);
        }
        console.error('Error fetching articles:', error);
        if (error.response?.status !== 401) {
          toast.error(language === 'fr' ? 'Erreur de chargement des articles' : 'Error loading articles');
        }
        // IMPORTANT: do NOT clear previous articles on transient errors — keeps UI stable
      } finally {
        setLoading(false);
      }
    };

    await attempt(1);
  }, [activeTab, language]);

  const fetchPendingComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/blog/comments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingComments(response.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/blog/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchArticles(controller.signal);
    fetchPendingComments();
    fetchStats();
    return () => controller.abort();
  }, [fetchArticles, fetchPendingComments, fetchStats]);

  // Open editor for new article
  const openNewEditor = () => {
    autoSavePausedRef.current = true;
    autoSaveDirtyRef.current = false;
    autoSaveArticleIdRef.current = null;
    setEditingArticle(null);
    setTitle('');
    setContent('');
    setExcerpt('');
    setImages([]);
    setStatus('draft');
    setScheduledFor('');
    setLastSavedAt(null);
    setAutoSaveStatus('idle');
    setEditorOpen(true);
    // Recherche d'un brouillon local non récupéré (cas crash/refresh)
    const localDraft = findUnsavedLocalDraft('new');
    if (localDraft) {
      setPendingLocalDraft({ ...localDraft, _origin: 'new' });
    } else {
      // pas de brouillon → on active l'auto-save après le 1er rendu
      setTimeout(() => { autoSavePausedRef.current = false; }, 800);
    }
  };

  // Open editor for existing article
  const openEditEditor = (article) => {
    autoSavePausedRef.current = true;
    autoSaveDirtyRef.current = false;
    autoSaveArticleIdRef.current = article.id;
    setEditingArticle(article);
    setTitle(article.title || '');
    setContent(article.content || '');
    setExcerpt(article.excerpt || '');
    // Convert old format to new
    if (article.images && article.images.length > 0) {
      setImages(article.images);
    } else if (article.image_url) {
      setImages([{ id: 'legacy_1', url: article.image_url, caption: null, order: 0 }]);
    } else {
      setImages([]);
    }
    setStatus(article.status || 'draft');
    // Convert ISO to local datetime-local value (strip timezone, trim seconds)
    if (article.scheduled_for) {
      try {
        const d = new Date(article.scheduled_for);
        const pad = (n) => String(n).padStart(2, '0');
        setScheduledFor(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } catch {
        setScheduledFor('');
      }
    } else {
      setScheduledFor('');
    }
    setLastSavedAt(null);
    setAutoSaveStatus('idle');
    setEditorOpen(true);
    // Récupération d'un brouillon local plus récent que la version backend
    const localDraft = findUnsavedLocalDraft(article.id);
    if (localDraft && new Date(localDraft.saved_at) > new Date(article.updated_at || 0)) {
      setPendingLocalDraft({ ...localDraft, _origin: 'edit' });
    } else {
      // Pas de divergence → on active l'auto-save (et on purge un éventuel
      // brouillon local obsolète)
      try { localStorage.removeItem(localDraftKey(article.id)); } catch (e) { /* ignore */ }
      setTimeout(() => { autoSavePausedRef.current = false; }, 800);
    }
  };

  // Restaurer un brouillon local en attente
  const restorePendingLocalDraft = () => {
    if (!pendingLocalDraft) return;
    setTitle(pendingLocalDraft.title || '');
    setContent(pendingLocalDraft.content || '');
    setExcerpt(pendingLocalDraft.excerpt || '');
    setImages(pendingLocalDraft.images || []);
    setStatus(pendingLocalDraft.status || 'draft');
    setScheduledFor(pendingLocalDraft.scheduledFor || '');
    if (pendingLocalDraft.article_id) {
      autoSaveArticleIdRef.current = pendingLocalDraft.article_id;
    }
    setPendingLocalDraft(null);
    setTimeout(() => {
      autoSavePausedRef.current = false;
      // Force un auto-save immédiat pour synchroniser le backend
      autoSaveDirtyRef.current = true;
      autoSaveDraftToBackend();
    }, 300);
    toast.success(language === 'fr' ? 'Brouillon restauré' : 'Draft restored');
  };

  const discardPendingLocalDraft = () => {
    if (pendingLocalDraft?._key) {
      try { localStorage.removeItem(pendingLocalDraft._key); } catch (e) { /* ignore */ }
    }
    setPendingLocalDraft(null);
    setTimeout(() => { autoSavePausedRef.current = false; }, 300);
  };

  const closeEditor = () => {
    // Vidage du brouillon local : on n'efface QUE si l'utilisateur ferme proprement
    // (clic « Annuler »). Les fermetures imprévues (refresh, perte session…)
    // laissent le brouillon dans localStorage → restauration possible.
    try {
      const k = localDraftKey(autoSaveArticleIdRef.current || editingArticle?.id || 'new');
      localStorage.removeItem(k);
    } catch (e) { /* ignore */ }
    autoSavePausedRef.current = true;
    autoSaveDirtyRef.current = false;
    autoSaveArticleIdRef.current = null;
    setAutoSaveStatus('idle');
    setLastSavedAt(null);
    setEditorOpen(false);
    setEditingArticle(null);
    setTitle('');
    setContent('');
    setExcerpt('');
    setImages([]);
    setStatus('draft');
    setScheduledFor('');
  };

  // ── Effet : sauvegarde miroir en localStorage à chaque modification ─────
  useEffect(() => {
    if (!editorOpen || autoSavePausedRef.current) return;
    if (!title && !content) return;
    try {
      const id = autoSaveArticleIdRef.current || editingArticle?.id || 'new';
      const snapshot = {
        title,
        content,
        excerpt,
        images,
        status,
        scheduledFor,
        article_id: autoSaveArticleIdRef.current || editingArticle?.id || null,
        saved_at: new Date().toISOString(),
      };
      localStorage.setItem(localDraftKey(id), JSON.stringify(snapshot));
      autoSaveDirtyRef.current = true;
      setAutoSaveStatus('pending');
    } catch (e) { /* quota, ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, excerpt, images, status, scheduledFor, editorOpen]);

  // ── Effet : auto-save backend toutes les 45 s si modif en attente ──────
  useEffect(() => {
    if (!editorOpen) {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      return;
    }
    if (autoSaveTimerRef.current) return; // déjà actif
    autoSaveTimerRef.current = setInterval(() => {
      // Conditions requises : éditeur ouvert, modification pendante,
      // titre + contenu non vides (sinon backend rejette pour min_length).
      if (autoSavePausedRef.current) return;
      if (!autoSaveDirtyRef.current) return;
      if (!title || title.trim().length < 3) return;
      if (!content || content.trim().length < 10) return;
      autoSaveDraftToBackend();
    }, 45000);
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen]);

  // Auto-save vers le backend en statut "draft" (jamais published)
  const autoSaveDraftToBackend = async () => {
    if (autoSaveStatus === 'saving') return;
    setAutoSaveStatus('saving');
    try {
      const token = localStorage.getItem('dermascan_token');
      // Important : l'auto-save n'altère JAMAIS le statut publié de l'utilisateur.
      // Si l'article est déjà "published" ou "scheduled", on conserve son statut
      // d'origine côté backend (les modifications sont enregistrées mais
      // l'article reste publié/programmé). Pour un NOUVEL article ou un article
      // déjà en draft, on force "draft".
      const originalStatus = editingArticle?.status;
      const persistStatus = (originalStatus === 'published' || originalStatus === 'scheduled')
        ? originalStatus
        : 'draft';
      const payload = {
        title,
        content,
        excerpt: excerpt || undefined,
        images: images.length > 0 ? images : undefined,
        image_url: images.length > 0 ? images[0].url : undefined,
        status: persistStatus,
        // L'auto-save ne touche pas à scheduled_for s'il est valide
        scheduled_for: persistStatus === 'scheduled' && scheduledFor
          ? new Date(scheduledFor).toISOString() : null,
      };
      const headers = { Authorization: `Bearer ${token}` };
      const effectiveId = autoSaveArticleIdRef.current || editingArticle?.id;
      if (effectiveId) {
        await axios.put(`${API_URL}/blog/articles/${effectiveId}`, payload, { headers });
      } else {
        // Premier auto-save d'un nouvel article → on crée en draft
        const r = await axios.post(`${API_URL}/blog/articles`, { ...payload, status: 'draft' }, { headers });
        const newId = r.data?.article_id;
        if (newId) {
          autoSaveArticleIdRef.current = newId;
          // Purge l'ancienne clé localStorage "new" : on bascule sur la clé par id
          try { localStorage.removeItem(localDraftKey('new')); } catch (e) { /* ignore */ }
        }
      }
      autoSaveDirtyRef.current = false;
      setLastSavedAt(new Date());
      setAutoSaveStatus('saved');
    } catch (e) {
      // Erreur réseau : on garde la sauvegarde locale, on signale visuellement
      console.warn('Autosave failed:', e?.response?.data?.detail || e?.message);
      setAutoSaveStatus('error');
    }
  };

  // Handle file selection
  const handleFileSelect = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} n'est pas une image`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} dépasse 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: 'new_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
          url: e.target.result,
          caption: null,
          order: images.length
        };
        setImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  // Paste handler
  const handlePaste = useCallback((e) => {
    if (!editorOpen) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleFileSelect([file]);
          e.preventDefault();
        }
      }
    }
  }, [editorOpen, images.length]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // Remove image
  const handleRemoveImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId).map((img, idx) => ({ ...img, order: idx })));
  };

  // Reorder images (drag within list)
  const handleImageDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);
    newImages.forEach((img, idx) => img.order = idx);
    
    setImages(newImages);
    setDraggedIndex(index);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save article
  const handleSaveArticle = async () => {
    if (!title.trim()) {
      toast.error(language === 'fr' ? 'Le titre est obligatoire' : 'Title is required');
      return;
    }
    if (!content.trim()) {
      toast.error(language === 'fr' ? 'Le contenu est obligatoire' : 'Content is required');
      return;
    }

    // Guardrail: scheduled requires a future date
    if (status === 'scheduled') {
      if (!scheduledFor) {
        toast.error(language === 'fr' ? 'Veuillez choisir une date de programmation' : 'Please pick a schedule date');
        return;
      }
      if (new Date(scheduledFor).getTime() <= Date.now()) {
        toast.error(language === 'fr' ? 'La date de programmation doit être dans le futur' : 'Schedule date must be in the future');
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const payload = {
        title,
        content,
        excerpt: excerpt || undefined,
        images: images.length > 0 ? images : undefined,
        image_url: images.length > 0 ? images[0].url : undefined,
        status,
        scheduled_for: status === 'scheduled' ? new Date(scheduledFor).toISOString() : null,
      };

      if (editingArticle) {
        await axios.put(`${API_URL}/blog/articles/${editingArticle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'fr' ? 'Article mis à jour' : 'Article updated');
      } else {
        await axios.post(`${API_URL}/blog/articles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'fr' ? 'Article créé' : 'Article created');
      }

      closeEditor();
      fetchArticles();
      fetchStats();
    } catch (error) {
      console.error('Error saving article:', error);
      const detail = error.response?.data?.detail
        || error.response?.data?.message
        || error.message
        || 'Erreur inconnue';
      const httpStatus = error.response?.status ? ` (HTTP ${error.response.status})` : '';
      toast.error(
        (language === 'fr' ? 'Erreur enregistrement' : 'Error saving') + httpStatus + ' : ' + detail,
        { duration: 8000 }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!articleToDelete) return;

    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/blog/articles/${articleToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Article supprimé' : 'Article deleted');
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting');
    }
  };

  const handleSeedStarterArticles = async () => {
    const confirmMsg = language === 'fr'
      ? 'Importer les 3 articles SEO de démarrage ? Les articles déjà présents (même titre) seront ignorés.'
      : 'Import the 3 starter SEO articles? Existing articles (same title) will be skipped.';
    if (!window.confirm(confirmMsg)) return;

    setSeeding(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.post(
        `${API_URL}/blog/admin/seed-starter-articles`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { created, skipped } = response.data;
      toast.success(
        language === 'fr'
          ? `${created} article(s) importé(s), ${skipped} déjà existant(s)`
          : `${created} article(s) imported, ${skipped} already existed`
      );
      fetchArticles();
      fetchStats();
    } catch (error) {
      console.error('Error seeding articles:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'import' : 'Error during import');
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleStatus = async (article) => {    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(`${API_URL}/blog/articles/${article.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === 'published' 
        ? (language === 'fr' ? 'Article publié' : 'Article published')
        : (language === 'fr' ? 'Article mis en brouillon' : 'Article unpublished'));
      fetchArticles();
      fetchStats();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(`${API_URL}/blog/comments/${commentId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Commentaire approuvé' : 'Comment approved');
      fetchPendingComments();
      fetchStats();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleRejectComment = async (commentId) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(`${API_URL}/blog/comments/${commentId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Commentaire rejeté' : 'Comment rejected');
      fetchPendingComments();
      fetchStats();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-[#0EA5E9]" />
              <p className="text-2xl font-bold text-[#0F172A]">{stats.total_articles}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Articles' : 'Articles'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Eye className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-[#0F172A]">{stats.total_views}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Vues' : 'Views'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-orange-500" />
              <p className="text-2xl font-bold text-[#0F172A]">{stats.total_comments}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Commentaires' : 'Comments'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Heart className="w-8 h-8 mx-auto mb-2 text-red-500" />
              <p className="text-2xl font-bold text-[#0F172A]">{stats.total_reactions}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Réactions' : 'Reactions'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Comments Alert */}
      {pendingComments.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-orange-500" />
                <span className="text-orange-700 font-medium">
                  {pendingComments.length} {language === 'fr' ? 'commentaire(s) en attente de modération' : 'comment(s) pending moderation'}
                </span>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {pendingComments.slice(0, 3).map(comment => (
                <div key={comment.id} className="bg-white rounded-lg p-3 flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{comment.author_name}</p>
                    <p className="text-xs text-gray-500 mb-1">sur "{comment.article_title}"</p>
                    <p className="text-sm text-gray-700 line-clamp-2">{comment.content}</p>
                  </div>
                  <div className="flex gap-2 ml-3">
                    <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleApproveComment(comment.id)}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleRejectComment(comment.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0EA5E9]" />
            {language === 'fr' ? 'Gestion du Blog' : 'Blog Management'}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={handleSeedStarterArticles}
              variant="outline"
              disabled={seeding}
              data-testid="blog-seed-starter-articles-btn"
              title={language === 'fr' ? 'Importer automatiquement les 3 articles SEO de démarrage' : 'Auto-import the 3 starter SEO articles'}
            >
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {language === 'fr' ? 'Importer les 3 articles SEO' : 'Import 3 starter articles'}
            </Button>
            <Button onClick={openNewEditor} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              <Plus className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouvel article' : 'New article'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">{language === 'fr' ? 'Tous' : 'All'}</TabsTrigger>
              <TabsTrigger value="published">{language === 'fr' ? 'Publiés' : 'Published'}</TabsTrigger>
              <TabsTrigger value="scheduled">{language === 'fr' ? 'Programmés' : 'Scheduled'}</TabsTrigger>
              <TabsTrigger value="draft">{language === 'fr' ? 'Brouillons' : 'Drafts'}</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-8 text-[#78716C]">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'fr' ? 'Aucun article' : 'No articles'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {articles.map(article => (
                    <div key={article.id} className="flex gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      {/* Thumbnail - show first image */}
                      <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                        {(article.images?.[0]?.url || article.image_url) ? (
                          <img 
                            src={article.images?.[0]?.url || article.image_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-medium text-[#0F172A] line-clamp-1">{article.title}</h3>
                            <p className="text-sm text-[#78716C] line-clamp-2 mt-1">{article.excerpt}</p>
                          </div>
                          <Badge variant={article.status === 'published' ? 'default' : article.status === 'scheduled' ? 'outline' : 'secondary'}
                            className={article.status === 'scheduled' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}>
                            {article.status === 'published'
                              ? (language === 'fr' ? 'Publié' : 'Published')
                              : article.status === 'scheduled'
                                ? `${language === 'fr' ? 'Programmé' : 'Scheduled'}${article.scheduled_for ? ' · ' + new Date(article.scheduled_for).toLocaleString('fr-FR', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : ''}`
                                : (language === 'fr' ? 'Brouillon' : 'Draft')}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-4 mt-3 text-sm text-[#78716C]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(article.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {article.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {Object.values(article.reactions || {}).reduce((a, b) => a + b, 0)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {article.comment_count || 0}
                          </span>
                          {article.images?.length > 1 && (
                            <span className="flex items-center gap-1">
                              <ImageIcon className="w-4 h-4" />
                              {article.images.length} photos
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => openEditEditor(article)}>
                            <Pencil className="w-4 h-4 mr-1" />
                            {language === 'fr' ? 'Modifier' : 'Edit'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className={article.status === 'published' ? 'text-orange-600' : 'text-green-600'}
                            onClick={() => handleToggleStatus(article)}
                          >
                            {article.status === 'published' ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Dépublier' : 'Unpublish'}
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Publier' : 'Publish'}
                              </>
                            )}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => { setArticleToDelete(article); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Article Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
            <DialogTitle>
              {editingArticle ? (language === 'fr' ? 'Modifier l\'article' : 'Edit article') : (language === 'fr' ? 'Nouvel article' : 'New article')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-gray-700">{language === 'fr' ? 'Titre' : 'Title'} *</label>
              <Input 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={language === 'fr' ? 'Titre de l\'article' : 'Article title'}
                className="mt-1"
              />
            </div>

            {/* Images - Drag & Drop Zone */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {language === 'fr' ? 'Images' : 'Images'}
                <span className="text-gray-400 font-normal ml-2">(Glissez-déposez ou collez depuis le presse-papiers)</span>
              </label>
              
              {/* Drop Zone */}
              <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-4 transition-all ${
                  isDragging 
                    ? 'border-[#0EA5E9] bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {/* Images Grid */}
                {images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                    {images.map((img, index) => (
                      <div
                        key={img.id}
                        draggable
                        onDragStart={() => handleImageDragStart(index)}
                        onDragOver={(e) => handleImageDragOver(e, index)}
                        onDragEnd={handleImageDragEnd}
                        className={`relative group aspect-square rounded-lg overflow-hidden border-2 cursor-move ${
                          index === 0 ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9]/30' : 'border-gray-200'
                        } ${draggedIndex === index ? 'opacity-50' : ''}`}
                      >
                        <img 
                          src={img.url} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Cover badge */}
                        {index === 0 && (
                          <div className="absolute top-1 left-1 bg-[#0EA5E9] text-white text-xs px-2 py-0.5 rounded">
                            Couverture
                          </div>
                        )}
                        
                        {/* Drag handle */}
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-white/90 rounded p-1">
                            <GripVertical className="w-4 h-4 text-gray-600" />
                          </div>
                        </div>
                        
                        {/* Actions overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => setPreviewImage(img)}
                            className="p-2 bg-white rounded-full hover:bg-gray-100"
                          >
                            <ZoomIn className="w-4 h-4 text-gray-700" />
                          </button>
                          <button
                            onClick={() => handleRemoveImage(img.id)}
                            className="p-2 bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload prompt */}
                <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
                  <Upload className={`w-10 h-10 mb-2 ${isDragging ? 'text-[#0EA5E9]' : 'text-gray-400'}`} />
                  <span className="text-sm text-gray-600">
                    {isDragging 
                      ? (language === 'fr' ? 'Déposez les images ici' : 'Drop images here')
                      : (language === 'fr' ? 'Cliquez ou glissez-déposez des images' : 'Click or drag & drop images')
                    }
                  </span>
                  <span className="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 5MB chacune</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)} 
                    className="hidden" 
                  />
                </label>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                💡 La première image sera utilisée comme couverture. Glissez pour réorganiser.
              </p>

              {/* Ajout par URL — recommandé pour l'aperçu WhatsApp */}
              <div className="mt-3 p-3 border border-emerald-200 bg-emerald-50/40 rounded-lg">
                <label className="text-xs font-semibold text-emerald-800 block mb-1">
                  🌐 Ajouter par URL (recommandé pour partage WhatsApp / Facebook)
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Collez une URL d'image (Unsplash, Pexels, lien direct vers .jpg/.png) pour qu'elle s'affiche en aperçu lors des partages.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="text-sm"
                    data-testid="blog-image-url-input"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const url = imageUrlInput.trim();
                      if (!url) return;
                      if (!/^https?:\/\//i.test(url)) {
                        toast.error("L'URL doit commencer par https://");
                        return;
                      }
                      const newImage = {
                        id: 'url_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                        url,
                        caption: null,
                        order: images.length,
                      };
                      setImages(prev => [...prev, newImage]);
                      setImageUrlInput('');
                      toast.success('Image ajoutée depuis URL');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                    data-testid="blog-image-url-add-btn"
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            </div>

            {/* Excerpt */}
            <div>
              <label className="text-sm font-medium text-gray-700">{language === 'fr' ? 'Extrait (aperçu)' : 'Excerpt (preview)'}</label>
              <Input 
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder={language === 'fr' ? 'Court résumé de l\'article' : 'Short article summary'}
                className="mt-1"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">{language === 'fr' ? 'Contenu' : 'Content'} *</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setLinkPickerQuery(''); setLinkPickerOpen(true); }}
                  className="h-7 text-xs gap-1"
                  data-testid="insert-article-link-btn"
                >
                  <Link2 className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Insérer un lien vers un article' : 'Insert article link'}
                </Button>
              </div>
              <textarea 
                ref={contentTextareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={language === 'fr' ? 'Écrivez votre article ici...' : 'Write your article here...'}
                className="mt-1 w-full min-h-[200px] p-3 border rounded-md focus:ring-2 focus:ring-[#0EA5E9] focus:border-[#0EA5E9] outline-none resize-y"
              />
              <p className="mt-1 text-xs text-gray-500">
                {language === 'fr'
                  ? 'Astuce : pour un lien interne tapez '
                  : 'Tip: for an internal link type '}
                <code className="px-1 py-0.5 bg-gray-100 rounded text-gray-700">&lt;a href=&quot;/blog/slug-de-l-article&quot;&gt;Texte&lt;/a&gt;</code>
                {language === 'fr' ? ' ou utilisez le bouton ci-dessus.' : ' or use the button above.'}
              </p>
            </div>

            {/* Status */}
            <div>
              <label className="text-sm font-medium text-gray-700">{language === 'fr' ? 'Statut' : 'Status'}</label>
              <div className="mt-1 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="status" 
                    value="draft" 
                    checked={status === 'draft'}
                    onChange={(e) => setStatus(e.target.value)}
                    className="text-[#0EA5E9]"
                  />
                  <span className="text-sm">{language === 'fr' ? 'Brouillon' : 'Draft'}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="status" 
                    value="scheduled" 
                    checked={status === 'scheduled'}
                    onChange={(e) => setStatus(e.target.value)}
                    className="text-[#0EA5E9]"
                  />
                  <span className="text-sm">{language === 'fr' ? 'Programmer' : 'Schedule'}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="status" 
                    value="published" 
                    checked={status === 'published'}
                    onChange={(e) => setStatus(e.target.value)}
                    className="text-[#0EA5E9]"
                  />
                  <span className="text-sm">{language === 'fr' ? 'Publier maintenant' : 'Publish now'}</span>
                </label>
              </div>

              {status === 'scheduled' && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <label className="text-sm font-medium text-blue-900">
                    {language === 'fr' ? 'Date et heure de publication' : 'Publication date & time'}
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="w-full p-2 border border-blue-300 rounded-md bg-white text-sm"
                    data-testid="blog-scheduled-for"
                  />
                  <p className="text-xs text-blue-700">
                    {language === 'fr'
                      ? "L'article deviendra visible automatiquement sur le blog à cette date."
                      : 'The article will become visible on the blog automatically at this date.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="shrink-0 px-6 py-3 border-t border-zinc-200 flex items-center justify-between sm:justify-between gap-3 flex-wrap">
            {/* Indicateur d'auto-save discret à gauche */}
            <div className="flex items-center gap-1.5 text-xs text-zinc-500 min-w-0" data-testid="blog-autosave-indicator">
              {autoSaveStatus === 'saving' && (
                <>
                  <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />
                  <span>{language === 'fr' ? 'Sauvegarde en cours…' : 'Saving…'}</span>
                </>
              )}
              {autoSaveStatus === 'saved' && lastSavedAt && (
                <>
                  <Check className="w-3 h-3 text-emerald-600" />
                  <span className="text-emerald-700">
                    {language === 'fr' ? 'Brouillon sauvegardé · ' : 'Draft saved · '}
                    {lastSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </>
              )}
              {autoSaveStatus === 'pending' && (
                <span className="text-amber-700 italic">
                  {language === 'fr' ? 'Modifications non synchronisées…' : 'Unsynced changes…'}
                </span>
              )}
              {autoSaveStatus === 'error' && (
                <span className="text-red-700">
                  {language === 'fr' ? '⚠ Sauvegarde réseau échouée (gardée localement)' : '⚠ Network save failed (kept locally)'}
                </span>
              )}
              {autoSaveStatus === 'idle' && (
                <span className="opacity-60">
                  {language === 'fr' ? 'Sauvegarde auto activée' : 'Autosave on'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={closeEditor}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleSaveArticle} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="blog-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingArticle ? (language === 'fr' ? 'Mettre à jour' : 'Update') : (language === 'fr' ? 'Créer' : 'Create')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recovery dialog : brouillon local retrouvé après crash/refresh */}
      <Dialog open={!!pendingLocalDraft} onOpenChange={(o) => { if (!o) discardPendingLocalDraft(); }}>
        <DialogContent className="max-w-md" data-testid="blog-recovery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              {language === 'fr' ? 'Brouillon non enregistré retrouvé' : 'Unsaved draft found'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Un brouillon a été retrouvé suite à une fermeture imprévue de l\'application. Voulez-vous le reprendre ?'
                : 'A draft was found from an unexpected app closure. Resume it?'}
            </DialogDescription>
          </DialogHeader>
          {pendingLocalDraft && (
            <div className="my-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
              <p className="font-semibold text-amber-900 truncate" data-testid="blog-recovery-title">
                {pendingLocalDraft.title || (language === 'fr' ? '(Sans titre)' : '(Untitled)')}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {language === 'fr' ? 'Sauvegardé le : ' : 'Saved on: '}
                {pendingLocalDraft.saved_at ? new Date(pendingLocalDraft.saved_at).toLocaleString('fr-FR') : '—'}
              </p>
              <p className="text-xs text-amber-700">
                {language === 'fr'
                  ? `Contenu : ${(pendingLocalDraft.content || '').length} caractère(s)`
                  : `Content: ${(pendingLocalDraft.content || '').length} character(s)`}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={discardPendingLocalDraft} data-testid="blog-recovery-discard">
              {language === 'fr' ? 'Ignorer' : 'Discard'}
            </Button>
            <Button onClick={restorePendingLocalDraft} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="blog-recovery-restore">
              {language === 'fr' ? 'Reprendre le brouillon' : 'Restore draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black">
          {previewImage && (
            <img 
              src={previewImage.url} 
              alt="" 
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Internal Article Link Picker */}
      <Dialog open={linkPickerOpen} onOpenChange={setLinkPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="link-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Insérer un lien vers un article' : 'Insert article link'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Choisissez un article. Un lien HTML sera inséré dans le contenu à l\'endroit de votre curseur.'
                : 'Pick an article. An HTML link will be inserted at the cursor position.'}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={linkPickerQuery}
            onChange={(e) => setLinkPickerQuery(e.target.value)}
            placeholder={language === 'fr' ? 'Rechercher par titre…' : 'Search by title…'}
            data-testid="link-picker-search"
            autoFocus
          />
          <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
            {articles
              .filter((a) => {
                if (editingArticle && a.id === editingArticle.id) return false;
                const q = linkPickerQuery.trim().toLowerCase();
                if (!q) return true;
                return (a.title || '').toLowerCase().includes(q)
                  || (a.slug || '').toLowerCase().includes(q);
              })
              .slice(0, 50)
              .map((a) => (
                <button
                  type="button"
                  key={a.id}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                  data-testid={`link-picker-article-${a.slug || a.id}`}
                  onClick={() => {
                    const slug = a.slug || a.id;
                    const label = (a.title || '').replace(/[<>]/g, '').trim() || slug;
                    const linkHtml = `<a href="/blog/${slug}">${label}</a>`;
                    const ta = contentTextareaRef.current;
                    if (ta) {
                      const start = ta.selectionStart ?? content.length;
                      const end = ta.selectionEnd ?? content.length;
                      const next = content.slice(0, start) + linkHtml + content.slice(end);
                      setContent(next);
                      // Replace cursor after the inserted link
                      requestAnimationFrame(() => {
                        try {
                          ta.focus();
                          const pos = start + linkHtml.length;
                          ta.setSelectionRange(pos, pos);
                        } catch (_) { /* ignore */ }
                      });
                    } else {
                      setContent(content + linkHtml);
                    }
                    setLinkPickerOpen(false);
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{a.title}</div>
                    <div className="text-xs text-gray-500 truncate">/blog/{a.slug || a.id}</div>
                  </div>
                  <Badge variant="outline" className="text-xs whitespace-nowrap">
                    {a.status === 'published'
                      ? (language === 'fr' ? 'Publié' : 'Published')
                      : (language === 'fr' ? 'Brouillon' : 'Draft')}
                  </Badge>
                </button>
              ))}
            {articles.filter((a) => !editingArticle || a.id !== editingArticle.id).length === 0 && (
              <div className="px-3 py-6 text-sm text-gray-500 text-center">
                {language === 'fr' ? 'Aucun autre article disponible.' : 'No other articles available.'}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkPickerOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Supprimer l\'article' : 'Delete article'}</DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Êtes-vous sûr de vouloir supprimer cet article ? Cette action est irréversible.'
                : 'Are you sure you want to delete this article? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteArticle}>
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManagement;
